// StepMappingEditor.jsx
//
// Param-first mapping editor for API chain branches.
//
// The mapping entry shape is:
//   { field, mapped_to, convert_to, mapping_template }
//
// Three ways to fill a parameter:
//   1. Pick a field   → sets `field`, clears `mapping_template`
//   2. Pick a value   → sets `mapping_template` to `{{ path }}`, clears `field`
//   3. Hardcoded      → sets `mapping_template` to `{{ "literal" }}`, clears `field`
//
// Runtime resolution (backend) prefers `mapping_template` when non-empty,
// otherwise falls back to `field` + `convert_to`.
//
// Layout direction: SOURCE → DESTINATION. The value is picked from the
// parent's response (left) and lands in the child's parameter (right).
// The arrow reads the direction the data flows.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaAngleDown, FaArrowRight, FaSync, FaUndo, FaInfoCircle } from "react-icons/fa";
import styles from "./StepMappingEditor.module.css";

// ================================================================
// Constants & helpers
// ================================================================

const TYPE_CONVERSIONS = {
  string:   { label: "String",   icon: "📝" },
  number:   { label: "Number",   icon: "🔢" },
  integer:  { label: "Integer",  icon: "🔢" },
  float:    { label: "Float",    icon: "💧" },
  boolean:  { label: "Boolean",  icon: "✓" },
  date:     { label: "Date",     icon: "📅" },
  datetime: { label: "DateTime", icon: "🕐" },
  array:    { label: "Array",    icon: "📋" },
  object:   { label: "Object",   icon: "📦" },
};

const COMPAT = {
  EXACT:   "exact",
  UNDER:   "under",
  OVER:    "over",
  MISSING: "missing",
};

const COMPAT_META = {
  [COMPAT.EXACT]:   { label: "Exact",   icon: "✅", color: "#48bb78" },
  [COMPAT.UNDER]:   { label: "Under",   icon: "⚠️", color: "#ed8936" },
  [COMPAT.OVER]:    { label: "Over",    icon: "🔵", color: "#4299e1" },
  [COMPAT.MISSING]: { label: "Missing", icon: "❌", color: "#e53e3e" },
};

const HARDCODED_TEMPLATE_RE = /^\{\{\s*(?:"([^"]*)"|'([^']*)')\s*\}\}$/;

function flattenObject(obj, prefix = "") {
  let result = {};
  for (let key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const newKey = prefix ? `${prefix}.${key}` : key;
    const val = obj[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      Object.assign(result, flattenObject(val, newKey));
    } else {
      result[newKey] = val;
    }
  }
  return result;
}

const extractPlaceholders = (template = "") => {
  const matches = String(template).match(/\{\{\s*([^}]+?)\s*\}\}/g) || [];
  return matches.map((m) => m.replace(/[\{\}\s]/g, ""));
};

const inferFieldType = (value) => {
  if (typeof value !== "string") {
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number")
      return Number.isInteger(value) ? "integer" : "float";
    if (value === null || value === undefined) return "string";
    return "string";
  }
  const t = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return "date";
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) return "string";
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(t)) return "datetime";
  if (/^(true|false)$/i.test(t)) return "boolean";
  if (/^\d+$/.test(t)) return "integer";
  if (/^\d+\.\d+$/.test(t)) return "float";
  return "string";
};

function renderTemplatePreview(template, fields) {
  if (!template || typeof template !== "string") return "";
  try {
    return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, expr) => {
      const trimmed = expr.trim();
      const strLit = trimmed.match(/^"([^"]*)"$/) || trimmed.match(/^'([^']*)'$/);
      if (strLit) return strLit[1];
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
      if (trimmed === "true" || trimmed === "false") return trimmed;

      const path = trimmed.split(".");
      let cur = fields;
      for (const p of path) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return "";
      }
      if (cur === undefined || cur === null) return "";
      if (typeof cur === "object") return JSON.stringify(cur);
      return String(cur);
    });
  } catch {
    return "";
  }
}

function classifyPreviewOutput(rendered) {
  if (!rendered) return { kind: "empty" };
  const s = rendered.trim();
  if (!s) return { kind: "empty" };
  const first = s[0];
  const looksJson =
    first === "{" ||
    first === "[" ||
    first === '"' ||
    s === "null" ||
    s === "true" ||
    s === "false" ||
    /^-?\d+(\.\d+)?$/.test(s);
  if (!looksJson) return { kind: "string", value: s };
  try {
    const parsed = JSON.parse(s);
    return { kind: "json", value: parsed };
  } catch {
    return { kind: "string", value: s };
  }
}

// ------------------------------------------------------------------
// API label formatting for the FROM / TO header.
// ------------------------------------------------------------------
const ABBREV_TAIL_SEGMENTS = 2;

function apiLabelParts(apiConfig) {
  if (!apiConfig || !apiConfig.url) {
    return { full: "", abbreviated: "" };
  }
  const method = (apiConfig.method || "GET").toUpperCase();

  let pathPart = apiConfig.url;
  const protocolMatch = pathPart.match(/^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i);
  if (protocolMatch) {
    pathPart = protocolMatch[1] || "/";
  }

  const qIndex = pathPart.indexOf("?");
  if (qIndex >= 0) pathPart = pathPart.slice(0, qIndex);
  const hIndex = pathPart.indexOf("#");
  if (hIndex >= 0) pathPart = pathPart.slice(0, hIndex);

  const segments = [];
  let buf = "";
  let i = 0;
  while (i < pathPart.length) {
    const ch = pathPart[i];
    if (ch === "/") {
      if (buf) {
        segments.push(buf);
        buf = "";
      }
      i += 1;
      continue;
    }
    if (ch === "{" && pathPart[i + 1] === "{") {
      const closeIdx = pathPart.indexOf("}}", i + 2);
      if (closeIdx === -1) {
        buf += pathPart.slice(i);
        break;
      }
      buf += pathPart.slice(i, closeIdx + 2);
      i = closeIdx + 2;
      continue;
    }
    buf += ch;
    i += 1;
  }
  if (buf) segments.push(buf);

  const fullPath = segments.length > 0 ? `/${segments.join("/")}` : "/";
  const tailSegments = segments.slice(-ABBREV_TAIL_SEGMENTS);
  const tail = tailSegments.join("/");
  const abbreviated = tail ? `${method} …/${tail}` : `${method} …`;

  return {
    full: `${method} ${fullPath}`,
    abbreviated,
  };
}

function parseBranchApiStructure(apiConfig) {
  const empty = { pathParams: [], queryParams: [], payloadParams: [] };
  if (!apiConfig) return empty;

  const structure = { pathParams: [], queryParams: [], payloadParams: [] };

  const samplePayload = (() => {
    const oc = apiConfig.original_curl_config || {};
    if (oc.payload && typeof oc.payload === "object") return oc.payload;
    try {
      return JSON.parse(apiConfig.payload_template || "{}");
    } catch {
      return {};
    }
  })();

  const getByPath = (obj, dotted) => {
    const parts = dotted.split(".");
    let cur = obj;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else return undefined;
    }
    return cur;
  };

  const url = apiConfig.url || "";
  (url.match(/\{\{\s*([^}]+?)\s*\}\}/g) || []).forEach((match) => {
    const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
    if (param && !structure.pathParams.some((p) => p.name === param)) {
      structure.pathParams.push({
        name: param,
        path: param,
        expectedType: "string",
        isStatic: false,
      });
    }
  });

  const queryParams = apiConfig.query_params || {};
  Object.keys(queryParams).forEach((key) => {
    const value = queryParams[key];
    if (typeof value === "string" && value.includes("{{")) {
      (value.match(/\{\{\s*([^}]+?)\s*\}\}/g) || []).forEach((match) => {
        const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
        if (param && !structure.queryParams.some((p) => p.name === param)) {
          structure.queryParams.push({
            name: param,
            path: key,
            expectedType: inferFieldType(value),
            isStatic: false,
          });
        }
      });
    } else if (!structure.queryParams.some((p) => p.name === key)) {
      structure.queryParams.push({
        name: key,
        path: key,
        expectedType: inferFieldType(value),
        isStatic: true,
      });
    }
  });

  const payloadTemplate = apiConfig.payload_template || "{}";
  try {
    const templateObj =
      typeof payloadTemplate === "string"
        ? JSON.parse(payloadTemplate)
        : payloadTemplate;

    const extractKeys = (obj, prefix = "") => {
      if (typeof obj !== "object" || obj === null) return;
      Object.keys(obj).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];
        if (typeof value === "string" && value.includes("{{")) {
          (value.match(/\{\{\s*([^}]+?)\s*\}\}/g) || []).forEach((match) => {
            const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
            if (param && !structure.payloadParams.some((p) => p.name === param)) {
              const sample =
                getByPath(samplePayload, param) ??
                getByPath(samplePayload, fullKey);
              structure.payloadParams.push({
                name: param,
                path: fullKey,
                template: value,
                expectedType: inferFieldType(sample),
                isStatic: false,
              });
            }
          });
        } else if (typeof value === "object" && value !== null) {
          extractKeys(value, fullKey);
        } else if (!structure.payloadParams.some((p) => p.name === key)) {
          structure.payloadParams.push({
            name: key,
            path: fullKey,
            value: value,
            expectedType: inferFieldType(value),
            isStatic: true,
          });
        }
      });
    };
    extractKeys(templateObj);
  } catch {
    (payloadTemplate.match(/\{\{\s*([^}]+?)\s*\}\}/g) || []).forEach(
      (match) => {
        const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
        if (param && !structure.payloadParams.some((p) => p.name === param)) {
          structure.payloadParams.push({
            name: param,
            path: param,
            template: match,
            expectedType: "string",
            isStatic: false,
          });
        }
      },
    );
  }

  return structure;
}

// ------------------------------------------------------------------
// Responsive-label hook. Returns 'wide' | 'medium' | 'narrow'.
// ------------------------------------------------------------------
function useBreakpoint() {
  const [tier, setTier] = useState(() => {
    if (typeof window === "undefined") return "wide";
    if (window.matchMedia("(max-width: 639px)").matches) return "narrow";
    if (window.matchMedia("(max-width: 1023px)").matches) return "medium";
    return "wide";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const narrowMq = window.matchMedia("(max-width: 639px)");
    const mediumMq = window.matchMedia("(max-width: 1023px)");

    const recompute = () => {
      if (narrowMq.matches) setTier("narrow");
      else if (mediumMq.matches) setTier("medium");
      else setTier("wide");
    };

    const subscribe = (mq, fn) =>
      mq.addEventListener ? mq.addEventListener("change", fn) : mq.addListener(fn);
    const unsubscribe = (mq, fn) =>
      mq.removeEventListener
        ? mq.removeEventListener("change", fn)
        : mq.removeListener(fn);

    subscribe(narrowMq, recompute);
    subscribe(mediumMq, recompute);
    return () => {
      unsubscribe(narrowMq, recompute);
      unsubscribe(mediumMq, recompute);
    };
  }, []);

  return tier;
}

// ================================================================
// Component
// ================================================================

const StepMappingEditor = ({
  stepIndex,
  previousStepApiConfigId,
  stepApiConfigId,
  mapping = {},
  onMappingChange,
  apiConfigs = [],
  statusCode = null,
  sampleResponseBody = null,
  leadFieldNames = [],
  requiredFieldNames,
  pathParamMappings = {},
  queryParamMappings = {},
  payloadParamMappings = {},
  onParamMappingsChange,
  overriddenByAppLevel = false,
  appScopedConfig = null,
}) => {
  const [availableResponseFields, setAvailableResponseFields] = useState([]);

  useEffect(() => {
    if (!sampleResponseBody) {
      setAvailableResponseFields([]);
      return;
    }
    let parsed;
    try {
      parsed =
        typeof sampleResponseBody === "string"
          ? JSON.parse(sampleResponseBody)
          : sampleResponseBody;
    } catch {
      setAvailableResponseFields([]);
      return;
    }
    if (!parsed || typeof parsed !== "object") {
      setAvailableResponseFields([]);
      return;
    }
    const flattened = flattenObject({ response_data: parsed });
    setAvailableResponseFields(
      Object.keys(flattened).map((key) => ({
        path: key,
        value: flattened[key],
      })),
    );
  }, [sampleResponseBody]);

  const hasNewMappings =
    Object.keys(pathParamMappings || {}).length > 0 ||
    Object.keys(queryParamMappings || {}).length > 0 ||
    Object.keys(payloadParamMappings || {}).length > 0 ||
    typeof onParamMappingsChange === "function";

  const branchApiConfig = useMemo(() => {
    if (!stepApiConfigId) return null;
    const cfg = apiConfigs.find((c) => c.api_config_id === stepApiConfigId);
    return cfg?.api_config || null;
  }, [stepApiConfigId, apiConfigs]);

  const breakpoint = useBreakpoint();

  const parentCfg = useMemo(() => {
    if (!previousStepApiConfigId) return null;
    const cfg = apiConfigs.find(
      (c) => c.api_config_id === previousStepApiConfigId,
    );
    return cfg?.api_config || null;
  }, [previousStepApiConfigId, apiConfigs]);

  const parentLabel = useMemo(() => apiLabelParts(parentCfg), [parentCfg]);
  const childLabel = useMemo(() => apiLabelParts(branchApiConfig), [branchApiConfig]);

  const sameEndpoint =
    parentCfg && branchApiConfig && parentCfg.url === branchApiConfig.url;

  const FULL_LABEL_MAX_CHARS = 60;

  const pickLabel = (parts, fallback) => {
    if (breakpoint === "narrow") return fallback;
    if (breakpoint === "medium") return parts.abbreviated || fallback;
    if (!parts.full) return fallback;
    if (parts.full.length > FULL_LABEL_MAX_CHARS) {
      return parts.abbreviated || fallback;
    }
    return parts.full;
  };

  const parentDisplay = pickLabel(parentLabel, "parent");
  const childDisplay = pickLabel(childLabel, "child");

  const structure = useMemo(
    () => parseBranchApiStructure(branchApiConfig),
    [branchApiConfig],
  );

  const [localPath, setLocalPath] = useState({});
  const [localQuery, setLocalQuery] = useState({});
  const [localPayload, setLocalPayload] = useState({});
  const [activeTab, setActiveTab] = useState("payload");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    if (hasNewMappings) {
      setLocalPath(pathParamMappings || {});
      setLocalQuery(queryParamMappings || {});
      setLocalPayload(payloadParamMappings || {});
      return;
    }
    const p = {};
    const q = {};
    const b = {};
    const struct = parseBranchApiStructure(branchApiConfig);
    const allPath = new Set(struct.pathParams.map((x) => x.name));
    const allQuery = new Set(struct.queryParams.map((x) => x.name));
    const allPayload = new Set(struct.payloadParams.map((x) => x.name));
    Object.entries(mapping || {}).forEach(([k, v]) => {
      const entry = {
        field: "",
        mapped_to: k,
        convert_to: "string",
        mapping_template: v || "",
      };
      if (allPath.has(k)) p[k] = entry;
      else if (allQuery.has(k)) q[k] = entry;
      else if (allPayload.has(k)) b[k] = entry;
      else b[k] = entry;
    });
    setLocalPath(p);
    setLocalQuery(q);
    setLocalPayload(b);
  }, [
    stepApiConfigId,
    pathParamMappings,
    queryParamMappings,
    payloadParamMappings,
    hasNewMappings,
  ]);

  const emitChange = useCallback(
    (p, q, b) => {
      if (typeof onParamMappingsChange === "function") {
        onParamMappingsChange(stepIndex, statusCode, {
          path: p,
          query: q,
          payload: b,
        });
      }
      if (typeof onMappingChange === "function") {
        const flat = {};
        [p, q, b].forEach((dict) =>
          Object.entries(dict).forEach(([k, entry]) => {
            const v = (entry && entry.mapping_template) || "";
            flat[entry?.mapped_to || k] = v;
          }),
        );
        onMappingChange(stepIndex, statusCode, null, null, null, flat);
      }
    },
    [stepIndex, statusCode, onParamMappingsChange, onMappingChange],
  );

  const handleParamChange = (location, paramName, updates) => {
    const setter =
      location === "path"
        ? setLocalPath
        : location === "query"
          ? setLocalQuery
          : setLocalPayload;
    const current =
      location === "path"
        ? localPath
        : location === "query"
          ? localQuery
          : localPayload;
    const next = {
      ...current,
      [paramName]: {
        ...(current[paramName] || {
          field: "",
          mapped_to: paramName,
          convert_to: "string",
          mapping_template: "",
        }),
        ...updates,
      },
    };
    setter(next);

    const np = location === "path" ? next : localPath;
    const nq = location === "query" ? next : localQuery;
    const nb = location === "payload" ? next : localPayload;
    emitChange(np, nq, nb);
  };

  const clearAll = () => {
    if (!window.confirm("Clear all mappings for this branch?")) return;
    setLocalPath({});
    setLocalQuery({});
    setLocalPayload({});
    emitChange({}, {}, {});
  };

  const toggleRow = (location, paramName) => {
    const key = `${location}:${paramName}`;
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const autoMap = () => {
    const autoFor = (params, dict) => {
      const next = { ...dict };
      params
        .filter((p) => !p.isStatic)
        .forEach((p) => {
          const leadMatch = leadFieldNames.find((n) => n === p.name);
          const respMatch = availableResponseFields.find(
            (f) => f.path === `response_data.${p.name}`,
          );
          let template = "";
          if (leadMatch) template = `{{ ${leadMatch} }}`;
          else if (respMatch) template = `{{ ${respMatch.path} }}`;
          if (!template) return;
          next[p.name] = {
            field: "",
            mapped_to: p.name,
            convert_to:
              p.expectedType && p.expectedType !== "string"
                ? p.expectedType
                : "string",
            mapping_template: template,
          };
        });
      return next;
    };

    const np = autoFor(structure.pathParams, localPath);
    const nq = autoFor(
      structure.queryParams.filter((p) => !p.isStatic),
      localQuery,
    );
    const nb = autoFor(
      structure.payloadParams.filter((p) => !p.isStatic),
      localPayload,
    );

    setLocalPath(np);
    setLocalQuery(nq);
    setLocalPayload(nb);
    emitChange(np, nq, nb);
  };

  const classifyParam = (param, dict) => {
    const current = dict[param.name];
    const hasTemplate = (current?.mapping_template || "").trim();
    if (hasTemplate) {
      return { compat: COMPAT.EXACT, reason: "Custom mapping template provided" };
    }
    const hasField = (current?.field || "").trim();
    if (!hasField) {
      return { compat: COMPAT.MISSING, reason: "Not mapped" };
    }
    return { compat: COMPAT.EXACT, reason: "Mapped" };
  };

  const sampleFields = useMemo(() => {
    const fields = {};
    leadFieldNames.forEach((name) => {
      const lower = name.toLowerCase();
      if (lower.includes("id") || lower.includes("count")) fields[name] = 1;
      else if (lower.includes("date")) fields[name] = "2026-01-01";
      else if (lower.includes("time")) fields[name] = "2026-01-01T00:00:00";
      else fields[name] = `<${name}>`;
    });
    availableResponseFields.forEach((f) => {
      fields[f.path] = f.value;
    });
    return fields;
  }, [leadFieldNames, availableResponseFields]);

    const renderRow = (location, param, dict) => {
    const rowKey = `${location}:${param.name}`;
    const isExpanded = !!expandedRows[rowKey];
    const entry = dict[param.name] || {};
    const template = entry.mapping_template || "";
    const hasTemplate = !!template.trim();
    const meta = COMPAT_META[classifyParam(param, dict).compat];

    const previewRendered = template
      ? renderTemplatePreview(template, sampleFields)
      : "";
    const previewClass = previewRendered
      ? classifyPreviewOutput(previewRendered)
      : { kind: "empty" };

    const hardcodedMatch = template.match(HARDCODED_TEMPLATE_RE);
    const hardcodedValue = hardcodedMatch
      ? hardcodedMatch[1] ?? hardcodedMatch[2]
      : null;

    const sourceOptions = [];
    leadFieldNames.forEach((n) =>
      sourceOptions.push({
        kind: "value",
        label: `{{ ${n} }}`,
        templateValue: `{{ ${n} }}`,
      }),
    );
    availableResponseFields.forEach((f) =>
      sourceOptions.push({
        kind: "value",
        label: `{{ ${f.path} }}`,
        templateValue: `{{ ${f.path} }}`,
      }),
    );

    let selectValue;
    if (hardcodedValue !== null) {
      selectValue = `__hardcoded_value__${hardcodedValue}`;
    } else if (entry.mapping_template) {
      selectValue = `__tpl__${entry.mapping_template}`;
    } else if (entry.field) {
      selectValue = `__field__${entry.field}`;
    } else {
      selectValue = "__none__";
    }

    const handleSourceChange = (v) => {
      if (v === "__none__") {
        handleParamChange(location, param.name, {
          field: "",
          mapping_template: "",
        });
        return;
      }
      if (v === "__hardcoded__") {
        const entered = window.prompt(
          `Enter the hardcoded value for "${param.name}".\n\n` +
            `It will be sent exactly as typed.`,
          hardcodedValue ?? "",
        );
        if (entered === null) return;
        const escaped = JSON.stringify(entered);
        handleParamChange(location, param.name, {
          mapping_template: `{{ ${escaped} }}`,
          field: "",
        });
        return;
      }
      if (v.startsWith("__tpl__")) {
        const tpl = v.slice(7);
        handleParamChange(location, param.name, {
          mapping_template: tpl,
          field: "",
          convert_to:
            param.expectedType && param.expectedType !== "string"
              ? param.expectedType
              : "string",
        });
        return;
      }

      if (v.startsWith("__field__")) {
        const bare = v.slice(9);
        handleParamChange(location, param.name, {
          field: bare,
          mapping_template: "",
        });
        return;
      }
    };

    return (
      <div key={rowKey} className={styles.mappingRow}>
        <div className={styles.mappingRowMain}>
          <div className={styles.mappingColumn}>
            <div className={styles.selectWrapperSmall}>
              <select
                className={styles.mappingSelect}
                value={selectValue}
                onChange={(e) => handleSourceChange(e.target.value)}
                title="Value from the parent's response"
              >
                <option value="__none__">-- pick a value --</option>

                {hardcodedValue !== null && (
                  <option value={`__hardcoded_value__${hardcodedValue}`}>
                    ✏️ Hardcoded: "{hardcodedValue}"
                  </option>
                )}

                {sourceOptions.map((o) => (
                  <option
                    key={o.templateValue}
                    value={`__tpl__${o.templateValue}`}
                  >
                    {o.label}
                  </option>
                ))}

                <option value="__hardcoded__">✏️ Hardcoded value…</option>
              </select>
              <FaAngleDown className={styles.selectIconSmall} />
            </div>
          </div>

          <div className={styles.mappingColumn}>
            <FaArrowRight className={styles.mappingArrow} />
          </div>

          <div className={styles.mappingColumn}>
            <div className={styles.paramHeader}>
              <span className={styles.fieldName}>{param.name}</span>
              {param.expectedType && (
                <span className={styles.fieldType}>
                  ({param.expectedType})
                </span>
              )}
              <span
                className={styles.typeBadge}
                style={{ background: meta.color, color: "white" }}
                title={meta.reason}
              >
                {meta.icon} {meta.label}
              </span>
            </div>
          </div>

          <div className={styles.mappingColumn}>
            <div className={styles.selectWrapperSmall}>
              <select
                className={styles.conversionSelect}
                value={entry.convert_to || "string"}
                onChange={(e) =>
                  handleParamChange(location, param.name, {
                    convert_to: e.target.value,
                  })
                }
                title="Coerce the final value to this type before sending"
              >
                {Object.entries(TYPE_CONVERSIONS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.icon} {v.label}
                  </option>
                ))}
              </select>
              <FaAngleDown className={styles.selectIconSmall} />
            </div>
          </div>

          <div className={styles.mappingColumn}>
            <span className={`${styles.locationBadge} ${styles[location]}`}>
              {location === "path"
                ? "🔗 Path"
                : location === "query"
                  ? "🔍 Query"
                  : "📦 Body"}
            </span>
          </div>

          <div className={styles.mappingColumn}>
            <button
              type="button"
              onClick={() => toggleRow(location, param.name)}
              className={`${styles.templateToggleBtn} ${
                hasTemplate ? styles.templateToggleBtnActive : ""
              }`}
              title="Use a custom template"
            >
              {hasTemplate ? "📝 ✓" : "📝"}
            </button>
            <button
              type="button"
              onClick={() => {
                const next = { ...dict };
                delete next[param.name];
                const np = location === "path" ? next : localPath;
                const nq = location === "query" ? next : localQuery;
                const nb = location === "payload" ? next : localPayload;
                if (location === "path") setLocalPath(next);
                if (location === "query") setLocalQuery(next);
                if (location === "payload") setLocalPayload(next);
                emitChange(np, nq, nb);
              }}
              className={styles.removeButton}
              title="Clear this row"
            >
              <RiDeleteBin6Line size={14} />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className={styles.templateEditor}>
            <div className={styles.templateEditorHint}>
              Custom mapping template (Jinja/Nunjucks). When non-empty, this
              overrides the simple field selection above.
            </div>

            <textarea
              value={template}
              onChange={(e) =>
                handleParamChange(location, param.name, {
                  mapping_template: e.target.value,
                })
              }
              placeholder={`e.g. {"id": {{ response_data.id }}}  OR  {{ room_id }}-{{ response_data.id }}`}
              rows={2}
              className={styles.templateTextarea}
            />

            <div className={styles.templateFieldChips}>
              <span className={styles.templateFieldChipsLabel}>
                Initial payload:
              </span>
              {leadFieldNames.map((n) => (
                <button
                  key={`lead-${n}`}
                  type="button"
                  className={styles.templateFieldChip}
                  onClick={() =>
                    handleParamChange(location, param.name, {
                      mapping_template: (template || "") + `{{ ${n} }}`,
                    })
                  }
                >
                  {n}
                </button>
              ))}
            </div>

            <div className={styles.templateFieldChips}>
              <span className={styles.templateFieldChipsLabel}>
                Response fields:
              </span>
              {availableResponseFields.map((f) => (
                <button
                  key={`resp-${f.path}`}
                  type="button"
                  className={styles.templateFieldChip}
                  onClick={() =>
                    handleParamChange(location, param.name, {
                      mapping_template:
                        (template || "") + `{{ ${f.path} }}`,
                    })
                  }
                >
                  {f.path}
                </button>
              ))}
            </div>

            {template.trim() && (
              <div
                className={`${styles.templatePreview} ${
                  previewClass.kind === "json"
                    ? styles.templatePreviewJson
                    : previewClass.kind === "string"
                      ? styles.templatePreviewString
                      : styles.templatePreviewEmpty
                }`}
              >
                <div className={styles.templatePreviewLabel}>
                  Preview ({previewClass.kind}
                  {previewClass.kind === "json" ? " → object/array" : ""})
                </div>
                <code className={styles.templatePreviewValue}>
                  {previewRendered || "(empty)"}
                </code>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  const pathRows = structure.pathParams;
  const queryRows = structure.queryParams.filter((p) => !p.isStatic);
  const payloadRows = structure.payloadParams.filter((p) => !p.isStatic);

  const hasPath = pathRows.length > 0;
  const hasQuery = queryRows.length > 0;
  const hasPayload = payloadRows.length > 0;

  useEffect(() => {
    if (hasPayload) setActiveTab("payload");
    else if (hasQuery) setActiveTab("query");
    else if (hasPath) setActiveTab("path");
  }, [stepApiConfigId, hasPath, hasQuery, hasPayload]);

  const currentRows =
    activeTab === "path" ? pathRows : activeTab === "query" ? queryRows : payloadRows;
  const currentDict =
    activeTab === "path"
      ? localPath
      : activeTab === "query"
        ? localQuery
        : localPayload;

  if (!stepApiConfigId) {
    return (
      <div className={styles.mappingSection}>
        <p className={styles.mappingHint}>
          Select a follow-up API above to configure its input mapping.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.mappingSection}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <label style={{ fontWeight: 600 }}>
          Mapping for status {statusCode}
        </label>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {overriddenByAppLevel && appScopedConfig && (
            <span
              style={{
                fontSize: 11,
                color: "#6b46c1",
                backgroundColor: "#f5f3ff",
                border: "1px solid #c4b5fd",
                padding: "2px 8px",
                borderRadius: 10,
                display: "inline-flex",
                alignItems: "center",
                gap: 4              }}
              title={
                `This tenant config's parameters are shown here, but the ` +
                `runtime will call the app-scoped version instead ` +
                `(${appScopedConfig.api_config?.url || appScopedConfig.api_config_id}). ` +
                `Verify the parameter names match on both versions.`
              }
            >
              <FaInfoCircle size={10} /> Overridden
            </span>
          )}
          <button
            type="button"
            onClick={autoMap}
            className={styles.autoMapBtn}
            style={{ margin: 0 }}
          >
            <FaSync /> Auto-Map
          </button>
          <button
            type="button"
            onClick={clearAll}
            className={styles.autoMapBtn}
            style={{ margin: 0, background: "#fed7d7", color: "#9b2c2c" }}
          >
            <FaUndo /> Clear
          </button>
        </div>
      </div>

      <p className={styles.mappingHint}>
        The follow-up API decides what it needs. For each variable below, pick a
        source value, enter a hardcoded value, or open the{" "}
        <strong>📝</strong> template editor to customise the mapping.
      </p>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "payload" ? styles.tabActive : ""} ${!hasPayload ? styles.tabDisabled : ""}`}
          onClick={() => hasPayload && setActiveTab("payload")}
          disabled={!hasPayload}
        >
          In Request Body
          {hasPayload && <span className={styles.tabBadge}>{payloadRows.length}</span>}
        </button>
        <button
          className={`${styles.tab} ${activeTab === "query" ? styles.tabActive : ""} ${!hasQuery ? styles.tabDisabled : ""}`}
          onClick={() => hasQuery && setActiveTab("query")}
          disabled={!hasQuery}
        >
          In URL Query String
          {hasQuery && <span className={styles.tabBadge}>{queryRows.length}</span>}
        </button>
        <button
          className={`${styles.tab} ${activeTab === "path" ? styles.tabActive : ""} ${!hasPath ? styles.tabDisabled : ""}`}
          onClick={() => hasPath && setActiveTab("path")}
          disabled={!hasPath}
        >
          In URL Path
          {hasPath && <span className={styles.tabBadge}>{pathRows.length}</span>}
        </button>
      </div>

      {(leadFieldNames.length > 0 || availableResponseFields.length > 0) && (
        <div className={styles.fieldsRow}>
          {leadFieldNames.length > 0 && (
            <div className={styles.availableFieldsBox}>
              <strong>Initial Payload fields</strong>
              <div className={styles.fieldChips}>
                {leadFieldNames.map((n) => (
                  <span key={n} className={styles.fieldChip}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}
          {availableResponseFields.length > 0 && (
            <div className={styles.availableFieldsBox}>
              <strong>Response fields</strong>
              <div className={styles.fieldChips}>
                {availableResponseFields.map((f) => (
                  <span
                    key={f.path}
                    className={styles.fieldChip}
                    title={`Value: ${JSON.stringify(f.value)}`}
                  >
                    {f.path}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {currentRows.length > 0 && (
        <div className={styles.mappingHeader}>
          <div className={styles.mappingHeaderSide}>
            <span className={styles.mappingHeaderTag}>FROM</span>
            <span
              className={styles.mappingHeaderLabel}
              title={parentLabel.full || undefined}
            >
              {parentDisplay}
            </span>
          </div>
          <div className={styles.mappingHeaderArrow}>
            <FaArrowRight size={12} />
          </div>
          <div className={styles.mappingHeaderSide}>
            <span className={styles.mappingHeaderTag}>TO</span>
            <span
              className={styles.mappingHeaderLabel}
              title={
                sameEndpoint
                  ? parentLabel.full || undefined
                  : childLabel.full || undefined
              }
            >
              {sameEndpoint ? parentDisplay : childDisplay}
            </span>
          </div>
        </div>
      )}

      <div className={styles.mappingRows}>
        {currentRows.length === 0 ? (
          <p className={styles.mappingHint} style={{ padding: "12px 0" }}>
            No variables in this location for the follow-up API.
          </p>
        ) : (
          currentRows.map((p) => renderRow(activeTab, p, currentDict))
        )}
      </div>
    </div>
  );
};

export default StepMappingEditor;